import { Injectable } from '@angular/core';
import { dollarsToCents } from '@core/util/money';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClientWithAssociations,
	Matter,
	MatterFileType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { isNumber } from 'lodash';
import { StepMilestone } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { StepCalculationsBuilder } from './step-calculations.service';

@Injectable({
	providedIn: 'root',
})
export class SyncRequirementsService {
	constructor(private stepperBuilder: StepCalculationsBuilder) {}

	getSyncRequirements(
		mortgage: LLCTransaction,
		matter: Matter,
		matterMortgageId: string,
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): StepMilestone[] {
		if (!matter) {
			return [];
		}

		const borrowers = this.stepperBuilder.getBorrowersReconcileStatus(
			mortgage,
			matter.id,
			matterMortgageId,
			matterClients,
		);
		const _guarantors = this.stepperBuilder.getGuarantorsReconcileStatus(
			mortgage,
			matter.id,
			matterMortgageId,
			guarantors,
		);

		const result = [borrowers];
		if (_guarantors) {
			result.push(_guarantors);
		}

		if (
			matter?.fileType === MatterFileType.PURCHASE &&
			isNumber(mortgage?.data?.mortgage?.purchasePrice)
		) {
			const priceReconciled = this.getPriceReconciliation(
				mortgage,
				matter,
				matterMortgageId,
			);
			result.push(priceReconciled);
		}

		return result;
	}

	private getPriceReconciliation(
		mortgage: LLCTransaction,
		matter: Matter,
		matterMortgageId: string,
	): StepMilestone {
		const match =
			dollarsToCents(mortgage?.data?.mortgage?.purchasePrice) ===
			matter?.purchasePrice;

		const task = this.stepperBuilder.buildTaskToComplete(
			'Mortgage purchase price match',
			match,
			UrlService.Matter_Mortgages_Mortgage(matter.id, matterMortgageId),
			null,
			{ modal: 'assignSync' },
			false,
		);

		return this.stepperBuilder.buildItemsSectionToComplete(
			'Purchase price reconciled',
			[task],
		);
	}
}
